import { getModalDraft, saveModalDraft } from '@/core/api/modalDraft'
import { useAutosave } from '@/core/hooks/useAutosave'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import {
  Button,
  Callout,
  Field,
  FieldGroup,
  FieldRow,
  Icon,
  LoadingState,
  Radio,
  RadioGroup,
  ScrollBar,
  StackList,
  StackListItem,
  StackListValue,
  Textarea,
  toastError,
  toastSuccess,
} from 'admination-react'
import { useApp } from '@/core/state/appStore'
import { ApiError } from '@/core/api/http'
import { initI18n } from '@/core/i18n'
import { formatZeitpunkt } from '@/core/lib/dates'
import { ModalHeader, type TabOverrides } from '@/core/components/modal/ModalHeader'
import { AUFGABE } from '../../aufgabe'
import {
  ABSCHNITT_KEYS,
  erzeugeKompetenzbilanzierung,
  erzeugenFehlerText,
  getKompetenzbilanzierung,
  NIVEAUS,
  setzeSprachniveau,
  speichereKompetenzbilanzierung,
  type AbschnittKey,
  type Niveau,
  type Abschnitte,
  type GespeicherteAbschnitte,
} from '../../api/kompetenzbilanzierung'
import { isEingeschraenkt } from '../../api/teilnehmerakte-staff'
import { useUnterschriften } from '../unterschrift/use-unterschriften'
import { SprachniveauMarker, useSprachniveauStand } from '../sprachniveau/sprachniveau'
import { MassnahmeKopf } from '../massnahme/MassnahmeKopf'
import type { MassnahmeKopf as Massnahme } from '../../api/massnahme'
import { getSpalteStand, type SpalteStand, type SprachniveauQuellen, type SprachniveauStand } from '../../api/spalteStand'

const ABSCHNITT_TITEL: Record<AbschnittKey, string> = {
  berufliche_ausgangssituation: "Berufliche Ausgangssituation",
  persoenliche_soziale_kompetenzen: "Persönliche und soziale Kompetenzen",
  praktische_methodische_kompetenzen: "Praktische und methodische Kompetenzen",
  sprachliche_kompetenzen: "Sprachliche Kompetenzen",
  zusammenfassende_bewertung: "Zusammenfassende Kompetenzbewertung",
}

type Stand = {
  texte: Abschnitte
  gespeichert: boolean
  von?: string | null
  am?: string | null
  massnahme?: Massnahme | null
}

const saverName = (name: string | null | undefined): string | null =>
  name
    ?.split(/\s+/)
    .filter((teil) => !/^[-–]*$/.test(teil))
    .join(' ') || null

const standVon = (g: GespeicherteAbschnitte): Stand => ({
  texte: Object.fromEntries(ABSCHNITT_KEYS.map((k) => [k, g[k]])) as Abschnitte,
  gespeichert: true,
  von: saverName(g.gespeichert_von_name),
  am: g.gespeichert_am ?? null,
  massnahme: g.massnahme,
})

const speichernFehlerText = (e: unknown): string =>
  isEingeschraenkt(e)
    ? initI18n().t('akte.openRestricted', { ns: 'vergabeStaff' })
    : e instanceof ApiError && e.status === 502
      ? "Das Dokument konnte nicht erstellt werden."
      : "Die Kompetenzbilanzierung konnte nicht gespeichert werden."

const gelandet = (gelesen: GespeicherteAbschnitte | null, gesendet: Stand): boolean =>
  gelesen !== null &&
  ABSCHNITT_KEYS.every((k) => gelesen[k] === gesendet.texte[k]) &&
  Date.parse(gelesen.gespeichert_am ?? '') > (gesendet.am === undefined ? -Infinity : Date.parse(gesendet.am ?? ''))

export type Anpassung =
  | { schritt: 'laeuft'; niveau: Niveau; bogen: boolean }
  | { schritt: 'fertig'; niveau: Niveau }
  | { schritt: 'fehler'; niveau: Niveau; text: string; bogenGespeichert: boolean }

const anpassenFehlerText = (e: unknown): string =>
  isEingeschraenkt(e)
    ? initI18n().t('akte.openRestricted', { ns: 'vergabeStaff' })
    : e instanceof ApiError && e.status === 409 && e.detailText
      ? e.detailText
      : "Das Sprachniveau konnte nicht angepasst werden."

export type KompetenzbilanzierungStand = {
  geladen: boolean
  stand: Stand | null
  sprachniveau: SprachniveauStand | null
  quellen: Pick<SpalteStand, 'sprachniveau_quellen' | 'sprachniveau_abweichung_quellen'> | null
  anpassung: Anpassung | null
  laeuft: boolean
  fehler: string | null
  erstellen: () => Promise<void>
  anpassen: (niveau: Niveau) => Promise<void>
  neuErstellen: () => Promise<void>
  speichern: () => Promise<boolean>
  aendern: (key: AbschnittKey, text: string) => void
}

export function useKompetenzbilanzierung(coachingId: string | null): KompetenzbilanzierungStand {
  const { reloadDashboard } = useApp()
  const [geladen, setGeladen] = useState(false)
  const [stand, setStand] = useState<Stand | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [anpassung, setAnpassung] = useState<Anpassung | null>(null)
  const gelesen = useSprachniveauStand(coachingId, geladen ? (stand?.am ?? '') : null)
  const [spalte, setSpalte] = useState<SpalteStand | null>(null)
  useEffect(() => {
    if (gelesen) setSpalte(gelesen)
  }, [gelesen])
  const sprachniveau = stand?.gespeichert ? (spalte?.sprachniveau ?? null) : null
  const quellen = spalte?.sprachniveau_quellen ? spalte : null

  useEffect(() => {
    if (!coachingId) return
    let aktuell = true
    Promise.all([getKompetenzbilanzierung(coachingId), getModalDraft<Abschnitte>(`/kompetenzbilanzierung/${coachingId}/entwurf`)])
      .then(([gespeichert, entwurf]) => {
        if (!aktuell) return
        if (entwurf) setStand({ ...(gespeichert ? standVon(gespeichert) : {}), texte: entwurf, gespeichert: false })
        else if (gespeichert) setStand(standVon(gespeichert))
        setGeladen(true)
      })
      .catch(() => {
        if (!aktuell) return
        setFehler("Die Kompetenzbilanzierung konnte nicht geladen werden.")
      })
    return () => {
      aktuell = false
    }
  }, [coachingId])

  const { flush: flushDraft } = useAutosave(stand?.texte ?? null, geladen && stand !== null && !laeuft, (texte) => {
    if (!coachingId || !texte) return
    return saveModalDraft(`/kompetenzbilanzierung/${coachingId}/entwurf`, texte)
  }, 250, { form: 'kompetenzbilanzierung', record: coachingId ?? undefined, label: "Kompetenzbilanzierung", recover: (texte) => { if (texte) setStand((previous) => ({ ...previous, texte, gespeichert: false })) } })

  const erstellen = useCallback(async () => {
    if (!coachingId || laeuft) return
    setLaeuft(true)
    setFehler(null)
    setAnpassung(null)
    try {
      setStand({ texte: await erzeugeKompetenzbilanzierung(coachingId), gespeichert: false })
    } catch (e) {
      const text = erzeugenFehlerText(e)
      if (text) setFehler(text)
      else toastError("Die Kompetenzbilanzierung konnte nicht erstellt werden.")
    } finally {
      setLaeuft(false)
    }
  }, [coachingId, laeuft])

  const speichern = useCallback(async () => {
    if (!coachingId || !stand || laeuft) return false
    setLaeuft(true)
    setFehler(null)
    setAnpassung(null)
    try {
      await flushDraft()
      const abgelehnt = await speichereKompetenzbilanzierung(coachingId, stand.texte).then(
        () => null,
        (grund: unknown) => {
          if (!(grund instanceof TypeError) && !(grund instanceof ApiError && [503, 504].includes(grund.status))) throw grund
          return { grund }
        },
      )
      const gespeichert = await getKompetenzbilanzierung(coachingId).catch(() => null)
      if (abgelehnt && !gelandet(gespeichert, stand)) {
        setFehler(speichernFehlerText(abgelehnt.grund))
        return false
      }
      setStand(gespeichert ? standVon(gespeichert) : { ...stand, gespeichert: true, am: null, von: null })
      toastSuccess("Die Kompetenzbilanzierung wurde gespeichert.")
      reloadDashboard()
      return true
    } catch (e) {
      setFehler(speichernFehlerText(e))
      return false
    } finally {
      setLaeuft(false)
    }
  }, [coachingId, stand, reloadDashboard, flushDraft, laeuft])

  const uebernehmen = useCallback(
    async (id: string, niveau: Niveau) => {
      const gespeichert = await getKompetenzbilanzierung(id)
      if (gespeichert) setStand(standVon(gespeichert))
      setAnpassung({ schritt: 'fertig', niveau })
      reloadDashboard()
    },
    [reloadDashboard],
  )

  const anpassen = useCallback(
    async (niveau: Niveau) => {
      if (!coachingId || laeuft || niveau === spalte?.sprachniveau_quellen?.sprachlehrkraft) return
      setLaeuft(true)
      setFehler(null)
      setAnpassung({ schritt: 'laeuft', niveau, bogen: true })
      try {
        await flushDraft()
        if (await setzeSprachniveau(coachingId, niveau)) await uebernehmen(coachingId, niveau)
        else setAnpassung(null)
      } catch (e) {
        console.error("Sprachniveau anpassen fehlgeschlagen:", e)
        const neu = await getSpalteStand(coachingId).catch(() => null)
        if (neu) setSpalte(neu)
        const bogenGespeichert = neu?.sprachniveau_quellen?.sprachlehrkraft === niveau
        setAnpassung({
          schritt: 'fehler',
          niveau,
          bogenGespeichert,
          text: bogenGespeichert
            ? `Der Bewertungsbogen der Sprachlehrkraft ist mit Niveau ${niveau} gespeichert. Die Kompetenzbilanzierung konnte nicht neu erstellt werden.`
            : anpassenFehlerText(e),
        })
      } finally {
        setLaeuft(false)
      }
    },
    [coachingId, laeuft, spalte, flushDraft, uebernehmen],
  )

  const neuErstellen = useCallback(async () => {
    if (!coachingId || laeuft || anpassung?.schritt !== 'fehler') return
    const { niveau } = anpassung
    setLaeuft(true)
    setAnpassung({ schritt: 'laeuft', niveau, bogen: false })
    try {
      await speichereKompetenzbilanzierung(coachingId, await erzeugeKompetenzbilanzierung(coachingId))
      await uebernehmen(coachingId, niveau)
    } catch (e) {
      console.error("Kompetenzbilanzierung erneut erstellen fehlgeschlagen:", e)
      setAnpassung({ ...anpassung, text: erzeugenFehlerText(e) ?? speichernFehlerText(e) })
    } finally {
      setLaeuft(false)
    }
  }, [coachingId, laeuft, anpassung, uebernehmen])

  const aendern = useCallback(
    (key: AbschnittKey, text: string) => { if (!laeuft) setStand((s) => (s ? { ...s, texte: { ...s.texte, [key]: text } } : s)) },
    [laeuft],
  )

  return { geladen, stand, sprachniveau, quellen, anpassung, laeuft, fehler, erstellen, anpassen, neuErstellen, speichern, aendern }
}

export function KompetenzbilanzierungAbschnitte({ kb }: { kb: KompetenzbilanzierungStand }) {
  const { geladen, stand, sprachniveau, laeuft, fehler, aendern } = kb
  return (
    <>
      <SprachniveauBox kb={kb} />
      {!stand && (
        <div className="section">
          <div className="section-body">
            {!geladen && !fehler && (
              <Callout tone="info" role="status" aria-busy="true">
                Die Kompetenzbilanzierung wird geladen …</Callout>
            )}
            {laeuft && kb.anpassung?.schritt !== 'laeuft' && (
              <Callout tone="info" role="status">
                Die Kompetenzbilanzierung wird erstellt …</Callout>
            )}
            {fehler && (
              <Callout tone="error" role="alert">
                {fehler}
              </Callout>
            )}
          </div>
        </div>
      )}
      {stand && (
        <div className="section">
          <div className="section-body">
            {stand.massnahme !== undefined && (
              <MassnahmeKopf massnahme={stand.massnahme} testId="kompetenzbilanzierung-massnahme" />
            )}
            <SprachniveauMarker dokument="kompetenzbilanzierung" stand={sprachniveau?.kompetenzbilanzierung} />
            {stand.gespeichert && (
              <Callout tone="success" role="status" data-testid="kompetenzbilanzierung-gespeichert">
                {["Kompetenzbilanzierung gespeichert", stand.von && `von ${stand.von}`, stand.am && `am ${formatZeitpunkt(stand.am)}`]
                  .filter(Boolean)
                  .join(' ') + '.'}
              </Callout>
            )}
            {ABSCHNITT_KEYS.map((key) => (
              <Abschnitt key={key} titel={ABSCHNITT_TITEL[key]} text={stand.texte[key]} disabled={laeuft} onChange={(text) => aendern(key, text)} />
            ))}
            {fehler && <SpeichernFehler text={fehler} />}
          </div>
        </div>
      )}
    </>
  )
}

export function KompetenzbilanzierungAktion({ kb, onSave }: { kb: KompetenzbilanzierungStand; onSave: () => Promise<unknown> }) {
  return (
    <div className="pill">
      {kb.stand ? (
        <Button primaryAction disabled={kb.laeuft} onClick={() => void onSave()}>Dokument abschließen</Button>
      ) : (
        <Button primaryAction disabled={!kb.geladen || kb.laeuft} onClick={() => void kb.erstellen()}>Kompetenzbilanzierung erstellen</Button>
      )}
    </div>
  )
}

export function Kompetenzbilanzierung({ coachingId, tabs }: { coachingId: string | null; tabs?: TabOverrides }) {
  const { dispatch } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)
  const foerderplan = useUnterschriften(coachingId, 'foerderplan', "Förderplan")
  const kb = useKompetenzbilanzierung(coachingId)
  const beide = typeof foerderplan === 'object' && foerderplan !== null && foerderplan.teilnehmende && foerderplan.coach
  const weiter = foerderplan !== 'laden' && !beide

  return (
    <>
      <ModalHeader tabs={tabs} onClose={() => dispatch({ type: 'CLOSE' })} closeDisabled={kb.laeuft}>
        <div className="pill-cluster">
          <KompetenzbilanzierungAktion kb={kb} onSave={kb.speichern} />
          {weiter && (
            <div className="pill">
              <Button iconOnly title="Weiter zum Förderplan" disabled={kb.laeuft} onClick={() => dispatch({ type: 'CHANGE_PHASE', target: { ...AUFGABE.foerderplanErstellen.landing } })}>
                <Icon name="i-arrow-right" />
              </Button>
            </div>
          )}
        </div>
      </ModalHeader>
      <main className="form" data-scroll-host ref={scrollerRef}>
        <KompetenzbilanzierungAbschnitte kb={kb} />
      </main>
      <ScrollBar scrollerRef={scrollerRef} />
    </>
  )
}

export function useKompetenzbilanzierungGespeichert(coachingId: string | null): boolean | 'laden' | 'fehler' {
  const [gespeichert, setGespeichert] = useState<boolean | 'laden' | 'fehler'>('laden')
  useEffect(() => {
    setGespeichert('laden')
    if (!coachingId) return
    let aktuell = true
    getKompetenzbilanzierung(coachingId)
      .then((gelesen) => {
        if (aktuell) setGespeichert(gelesen !== null)
      })
      .catch((error: unknown) => {
        console.error("Kompetenzbilanzierung laden fehlgeschlagen:", error)
        if (aktuell) setGespeichert('fehler')
      })
    return () => {
      aktuell = false
    }
  }, [coachingId])
  return gespeichert
}

export function KompetenzbilanzierungSperre({ gespeichert, tabs }: { gespeichert: false | 'laden' | 'fehler'; tabs?: TabOverrides }) {
  const { dispatch } = useApp()
  return (
    <>
      <ModalHeader tabs={tabs} onClose={() => dispatch({ type: 'CLOSE' })} />
      <main className="form" data-scroll-host>
        <div className="section">
          <div className="section-body">
            {gespeichert === 'laden' && <LoadingState>Die Kompetenzbilanzierung wird geladen …</LoadingState>}
            {gespeichert === 'fehler' && (
              <Callout tone="error" role="alert">
                Die Kompetenzbilanzierung konnte nicht geladen werden.</Callout>
            )}
            {gespeichert === false && (
              <Callout tone="info" role="status" data-testid="kompetenzbilanzierung-fehlt">
                Erst möglich, wenn die Kompetenzbilanzierung erstellt ist.</Callout>
            )}
          </div>
        </div>
      </main>
    </>
  )
}

const QUELLE = [
  ['zertifikat', "Zertifikat Integrationskurs"],
  ['klett', "Klett-Test"],
  ['sprachlehrkraft', "Bewertungsbogen Sprachlehrkraft"],
] as const satisfies readonly (readonly [keyof SprachniveauQuellen, string])[]

const abweichungText = (q: SprachniveauQuellen): string =>
  `Die Angaben zum Sprachniveau weichen voneinander ab. ${QUELLE.flatMap(([quelle, name]) => (q[quelle] ? [`${name} ${q[quelle]}`] : [])).join(', ')}. ` +
  "Unterschiede sind zulässig. Wenn das Niveau der Sprachlehrkraft nicht stimmt, passen Sie es hier an."

function SprachniveauBox({ kb }: { kb: KompetenzbilanzierungStand }) {
  const [auswahl, setAuswahl] = useState<Niveau | null>(null)
  const quellen = kb.quellen?.sprachniveau_quellen
  if (!quellen) return null
  const abweichung = kb.quellen?.sprachniveau_abweichung_quellen
  const { anpassung, laeuft } = kb
  const bogen = NIVEAUS.find((niveau) => niveau === quellen.sprachlehrkraft)
  return (
    <div className="section" data-testid="kompetenzbilanzierung-sprachniveau">
      <h3 className="section-header">Sprachniveau</h3>
      <div className="section-body">
        <StackList>
          {QUELLE.map(([quelle, name]) => (
            <StackListItem key={quelle} end={<StackListValue>{quellen[quelle] ?? '–'}</StackListValue>}>
              {name}
            </StackListItem>
          ))}
        </StackList>
        {abweichung && (
          <Callout tone="error" role="alert" data-testid="sprachniveau-abweichung-kompetenzbilanzierung">
            {abweichungText(abweichung)}
          </Callout>
        )}
        {abweichung && bogen && auswahl === null && (
          <FieldRow>
            <Button disabled={laeuft} onClick={() => setAuswahl(bogen)}>Sprachlevel anpassen</Button>
          </FieldRow>
        )}
        {auswahl !== null && (
          <>
            <FieldGroup legend="Sprachniveau der Sprachlehrkraft" hint="Beim Übernehmen wird der Bewertungsbogen der Sprachlehrkraft mit dem gewählten Sprachniveau neu erstellt. Danach wird die Kompetenzbilanzierung neu erstellt." hintTone="info">
              <RadioGroup role="radiogroup">
                {NIVEAUS.map((niveau) => (
                  <Radio key={niveau} chip name="sprachniveau-anpassen" value={niveau} checked={auswahl === niveau} disabled={laeuft} onChange={() => setAuswahl(niveau)}>
                    {niveau}
                  </Radio>
                ))}
              </RadioGroup>
            </FieldGroup>
            <FieldRow>
              <Button variant="primary" disabled={laeuft} onClick={() => { setAuswahl(null); void kb.anpassen(auswahl) }}>Übernehmen</Button>
              <Button disabled={laeuft} onClick={() => setAuswahl(null)}>Abbrechen</Button>
            </FieldRow>
          </>
        )}
        {anpassung?.schritt === 'laeuft' && (
          <>
            {anpassung.bogen && (
              <Callout tone="info" role="status" aria-busy="true">
                {"Der Bewertungsbogen der Sprachlehrkraft wird mit Niveau "}{anpassung.niveau}{" neu erstellt …"}</Callout>
            )}
            <Callout tone="info" role="status" aria-busy="true">
              Die Kompetenzbilanzierung wird neu erstellt …</Callout>
          </>
        )}
        {anpassung?.schritt === 'fertig' && (
          <Callout tone="success" role="status">
            {"Bewertungsbogen der Sprachlehrkraft mit Niveau "}{anpassung.niveau}{" und Kompetenzbilanzierung neu erstellt."}</Callout>
        )}
        {anpassung?.schritt === 'fehler' && (
          <>
            <Callout tone="error" role="alert">
              {anpassung.text}
            </Callout>
            {anpassung.bogenGespeichert && (
              <FieldRow>
                <Button disabled={laeuft} onClick={() => void kb.neuErstellen()}>Kompetenzbilanzierung erneut erstellen</Button>
              </FieldRow>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function SpeichernFehler({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'nearest' })
  }, [text])
  return (
    <Callout ref={ref} tone="error" role="alert">
      {text}
    </Callout>
  )
}

function Abschnitt({ titel, text, disabled, onChange }: { titel: string; text: string; disabled: boolean; onChange: (text: string) => void }) {
  const id = useId()
  return (
    <Field label={titel} htmlFor={id}>
      <Textarea id={id} rows={1} value={text} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
    </Field>
  )
}
